import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Checkbox,
  Checkbox as AntCheckbox,
  Tabs,
  Button as AntButton,
  message,
  Table,
  Space,
  Popconfirm,
  Pagination,
} from 'antd';
import {
  LeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  getSubUserRoles,
  getSubUserRolesById,
  addSubUserRole,
  deletePermissionsInvoiceAll,
  deletePermissionsCostAll,
  deletePermissionsReportAll,
  getPermissionsInvoiceAdd,
  getPermissionsCostAdd,
  getPermissionsReportAdd,
  getPermissionsInvoiceDelete,
  getPermissionsCostDelete,
  getPermissionsReportDelete,
  getCostCheckRoleEquivalents,
  getInvoiceCheckRoleEquivalents,
  getReportCheckRoleEquivalents,
  setForAllViewInvoice,
  setForAllAddInvoice,
  setForAllUpdateInvoice,
  setForAllDeleteInvoice,
  setForAllViewCost,
  setForAllAddCost,
  setForAllUpdateCost,
  setForAllDeleteCost,
  setForAllViewReport,
  getExistingRolesCost,
  getExistingRolesInvoice,
  getExistingRolesReport,
  getCostRoles,
  getInvoiceRoles,
  getReportRoles,
} from 'services/sub-users';
import {
  getPartners,
  getDebitors,
  getCreditors,
  getReportsPermissions,
} from 'services/partners';
import { Button } from 'components/button';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { queryClient } from 'config/query-client';
import { ColumnType } from 'antd/lib/table';
import useTranslation from 'customHooks/translate';
import {
  CREDITORS,
  DEBITORS,
  PARTNERS,
  REPORTS,
  ROLE_BY_ID,
  SUB_USER_ROLES,
} from 'events/types';
import PermissionSubUserForm from 'pages/Dialogs/PermissionSubUserForm';

interface PartnerDataType {
  id: number;
  partnerName: string;
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

const initialPartnerState: PartnerDataType = {
  id: 0,
  partnerName: '',
  read: false,
  create: false,
  update: false,
  delete: false,
};

const PermissionSubUsers = () => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState<any>([]);
  const { id } = useParams();
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [permissionsData, setPermissionsData] = useState<any>([]);
  const [pageSize, setPageSize] = useState(5);
  const [pageIndex, setPageIndex] = useState(0);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [partnerData, setPartnerData] = useState<any>(initialPartnerState);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [total, setTotal] = useState();

  const { data: allRolesData } = useQuery<any, AxiosError, any[]>({
    queryKey: [SUB_USER_ROLES],
    queryFn: () => getSubUserRoles(),
    select: (response) => response.data.map((d) => ({ ...d, key: d.id })),
  });
  const { data: partners } = useQuery<any, AxiosError, any[]>({
    queryKey: [PARTNERS],
    queryFn: () => getPartners(),
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
  });
  const { data: debitors } = useQuery<any, AxiosError, any[]>({
    queryKey: [DEBITORS],
    queryFn: () => getDebitors(),
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
  });
  const { data: creditors } = useQuery<any, AxiosError, any[]>({
    queryKey: [CREDITORS],
    queryFn: () => getCreditors(),
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
  });

  const { data: reports } = useQuery<any, AxiosError, any[]>({
    queryKey: [REPORTS],
    queryFn: () => getReportsPermissions(),
    select: (response) => response.data.map((d) => ({ ...d, key: d.id })),
  });

  const selectedPartner = localStorage.getItem('subUserName');

  const fetchPermissionsData = async () => {
    if (!id) return;

    let data;
    try {
      switch (activeTabKey) {
        case '1': // Cost Tab
          data = await getCostRoles({
            pageSize,
            pageIndex,
            userId: id,
          });
          break;
        case '2': // Invoice Tab
          data = await getInvoiceRoles({
            pageSize,
            pageIndex,
            userId: id,
          });
          break;
        case '3': // Report Tab
          data = await getReportRoles({
            pageSize,
            pageIndex,
            userId: id,
          });
          break;
        default:
          data = [];
      }
      setPermissionsData(data?.data?.data || []);
      setTotal(data?.data?.totalRecord);
    } catch (error: any) {
      message.error(
        error?.response?.data.Message
          ? error?.response?.data.Message
          : 'Permission required!'
      );
      setPermissionsData([]);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setPageIndex(page - 1);
    if (size) {
      setPageSize(size);
    }
  };

  const handleDelete = async (record: any) => {
    try {
      if (!id) return;
      switch (activeTabKey) {
        case '1':
          await getPermissionsCostDelete(record.id);
          message.success(`${translation.subusers.deleteMessageCost}`);
          break;

        case '2':
          await getPermissionsInvoiceDelete(record.id);
          message.success(`${translation.subusers.deleteMessageInvoice}`);
          break;

        case '3':
          await getPermissionsReportDelete(record.id);
          message.success(`${translation.subusers.deleteMessageReport}`);
          break;

        default:
          throw new Error(`${translation.subusers.deleteError}`);
      }

      await fetchPermissionsData();
    } catch (error) {
      message.error(`${translation.subusers.deleteError2}`);
    }
  };

  const { data: roleById } = useQuery<any, AxiosError, any[]>({
    queryKey: [ROLE_BY_ID, id],
    queryFn: () => getSubUserRolesById(id),
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    cacheTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (id) {
      fetchPermissionsData();
    }
  }, [id, activeTabKey, pageIndex, pageSize]);

  const onTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  const getColumns = (activeTabKey: string): ColumnType<any>[] => {
    if (activeTabKey === '3') {
      return [
        {
          title: `${translation.subusers.reportname}`,
          dataIndex: 'reportName',
          key: 'reportName',
          align: 'center',
        },
        {
          title: `${translation.subusers.view}`,
          dataIndex: 'read',
          key: 'read',
          align: 'center',
          render: (text: boolean, record: any) => (
            <ReadOnlyCheckbox
              checked={text}
              $isReadOnly
              onChange={(e) =>
                handleCheckboxChange(record, 'read', e.target.checked)
              }
            />
          ),
        },
        {
          title: `${translation.subusers.action}`,
          key: 'action',
          align: 'center',
          render: (_, record) => (
            <Space size="middle">
              <EditOutlined
                onClick={() => openDrawer('edit', record)}
                style={{ color: '#1890ff' }}
              />
              <Popconfirm
                title={translation.subusers.deleteConfirm2}
                onConfirm={() => handleDelete(record)}
                okText={translation.InvoiceDetails.Yes}
                cancelText={translation.InvoiceDetails.No}
              >
                <DeleteOutlined style={{ color: '#ff4d4f' }} />
              </Popconfirm>
            </Space>
          ),
        },
      ];
    }

    return [
      {
        title: `${translation.subusers.partnerName}`,
        dataIndex: 'partnerName',
        key: 'partnerName',
        align: 'center',
      },
      {
        title: `${translation.subusers.view}`,
        dataIndex: 'read',
        key: 'read',
        align: 'center',
        render: (text: boolean, record: any) => (
          <ReadOnlyCheckbox
            checked={text}
            $isReadOnly
            onChange={(e) =>
              handleCheckboxChange(record, 'read', e.target.checked)
            }
          />
        ),
      },
      {
        title: `${translation.subusers.create}`,
        dataIndex: 'create',
        key: 'create',
        align: 'center',
        render: (text: boolean, record: any) => (
          <ReadOnlyCheckbox
            checked={text}
            $isReadOnly
            onChange={(e) =>
              handleCheckboxChange(record, 'create', e.target.checked)
            }
          />
        ),
      },
      {
        title: `${translation.subusers.update}`,
        dataIndex: 'update',
        key: 'update',
        align: 'center',
        render: (text: boolean, record: any) => (
          <ReadOnlyCheckbox
            checked={text}
            $isReadOnly
            onChange={(e) =>
              handleCheckboxChange(record, 'update', e.target.checked)
            }
          />
        ),
      },
      {
        title: `${translation.subusers.delete}`,
        dataIndex: 'delete',
        key: 'delete',
        align: 'center',
        render: (text: boolean, record: any) => (
          <ReadOnlyCheckbox
            checked={text}
            $isReadOnly
            onChange={(e) =>
              handleCheckboxChange(record, 'delete', e.target.checked)
            }
          />
        ),
      },
      {
        title: `${translation.subusers.action}`,
        key: 'action',
        align: 'center',
        render: (_, record) => (
          <Space size="middle">
            <EditOutlined
              onClick={() => openDrawer('edit', record)}
              style={{ color: '#1890ff' }}
            />
            <Popconfirm
              title={translation.subusers.deleteConfirm2}
              onConfirm={() => handleDelete(record)}
              okText={translation.InvoiceDetails.Yes}
              cancelText={translation.InvoiceDetails.No}
            >
              <DeleteOutlined style={{ color: '#ff4d4f' }} />
            </Popconfirm>
          </Space>
        ),
      },
    ];
  };

  const handleCheckboxChange = (record: any, key: string, value: boolean) => {
    setPermissionsData((prevPermissions) =>
      prevPermissions.map((item) =>
        item.userId === record.userId ? { ...item, [key]: value } : item
      )
    );
  };

  const handlePermissionChange = (
    roleGroup: string,
    permission: string,
    checked: boolean
  ) => {
    setPermissions((prev: any) => ({
      ...prev,
      [roleGroup]: {
        ...prev[roleGroup],
        [permission]: checked,
      },
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedPartner) return;
    const roles = new Set<string>();
    roleById?.forEach((role) => roles.add(role.roleName));

    Object.keys(permissions).forEach((roleGroup) => {
      Object.keys(permissions[roleGroup]).forEach((permission) => {
        if (permissions[roleGroup][permission]) {
          roles.add(permission);
        } else {
          roles.delete(permission);
        }
      });
    });
    const payload = {
      userName: selectedPartner,
      roles: Array.from(roles),
    };

    try {
      await addSubUserRole(payload);
      message.success(`${translation.subusers.deleteSuccess}`);
      queryClient.invalidateQueries(['subUserRolesById', id]);
    } catch (error: any) {
      message.error(error.response.data.Message);
    }
  };
  const [groupedRoles, setGroupedRoles] = useState<{ [key: string]: string[] }>(
    {}
  );

  const language = localStorage.getItem('language');

  useEffect(() => {
    if (!allRolesData) return;

    const grouped = allRolesData.reduce((acc, role) => {
      const { roleGroup, name } = role;

      if (['Admin', 'Reports', 'Invoices', 'Costs'].includes(roleGroup))
        return acc;

      if (!acc[roleGroup]) {
        acc[roleGroup] = [];
      }

      acc[roleGroup].push(name);

      return acc;
    }, {} as { [key: string]: string[] });

    setGroupedRoles(grouped);
  }, [allRolesData, language]);

  const handleAddNew = () => {
    openDrawer('add');
  };

  const handleDeleteAll = async () => {
    if (!id) return;

    try {
      switch (activeTabKey) {
        case '1':
          await deletePermissionsCostAll(id);
          message.success(`${translation.subusers.deleteCostAll}`);
          break;
        case '2':
          await deletePermissionsInvoiceAll(id);
          message.success(`${translation.subusers.deleteInvoiceAll}`);
          break;
        case '3':
          await deletePermissionsReportAll(id);
          message.success(`${translation.subusers.deleteReportAll}`);
          break;
        default:
          throw new Error(`${translation.subusers.deleteAllError}`);
      }
      await fetchPermissionsData();
    } catch (error: any) {
      message.error(
        error?.response?.data.Message
          ? error?.response?.data.Message
          : `${translation.subusers.deleteAllError2}`
      );
    }
  };

  const handleSavePermissionsModal = async () => {
    const payload = {
      ...(modalMode === 'edit' && { id: partnerData.id }),
      create: partnerData.create,
      read: partnerData.read,
      update: partnerData.update,
      delete: partnerData.delete,
      userId: id,
      ...(activeTabKey === '3'
        ? {
            reportId:
              modalMode === 'add' || modalMode === 'edit'
                ? [partnerData.reportId]
                : partnerData.reportId,
          }
        : {
            partnerId:
              modalMode === 'add' || modalMode === 'edit'
                ? [partnerData.partnerId]
                : partnerData.partnerId,
          }),
    };

    try {
      if (modalMode === 'add') {
        switch (activeTabKey) {
          case '1':
            await getPermissionsCostAdd(payload);
            message.success(`${translation.subusers.addSucces}`);
            break;
          case '2':
            await getPermissionsInvoiceAdd(payload);
            message.success(`${translation.subusers.addSucces2}`);
            break;
          case '3':
            await getPermissionsReportAdd(payload);
            message.success(`${translation.subusers.addSucces3}`);
            break;
          default:
            throw new Error(`${translation.subusers.addError}`);
        }
      } else if (modalMode === 'edit') {
        switch (activeTabKey) {
          case '1':
            await getPermissionsCostAdd(payload);
            message.success(`${translation.subusers.updateSucces}`);
            break;
          case '2':
            await getPermissionsInvoiceAdd(payload);
            message.success(`${translation.subusers.updateSucces2}`);
            break;
          case '3':
            await getPermissionsReportAdd(payload);
            message.success(`${translation.subusers.updateSucces3}`);
            break;
          default:
            throw new Error(`${translation.subusers.addError}`);
        }
      }
      setIsDrawerVisible(false);
      await fetchPermissionsData();
    } catch (error: any) {
      message.error(
        error?.response?.data.Message
          ? error?.response?.data.Message
          : `${translation.subusers.deleteSuccess2}`
      );
    }
  };

  const handleInputChange = async (key: string, value: any) => {
    setPartnerData((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key === 'partnerId' || key === 'reportId') {
      let response;
      try {
        switch (activeTabKey) {
          case '1': // Cost Tab
            response = await getExistingRolesCost(id, value);
            break;
          case '2': // Invoice Tab
            response = await getExistingRolesInvoice(id, value);
            break;
          case '3': // Report Tab
            response = await getExistingRolesReport(id, value);
            break;
          default:
            return;
        }
        const data = response?.data?.data;
        setPartnerData((prev) => ({
          ...prev,
          read: data?.read || false,
          create: data?.create || false,
          update: data?.update || false,
          delete: data?.delete || false,
        }));
      } catch (error: any) {
        message.error(error?.response?.data.Message);
      }
    }
  };

  const openDrawer = async (mode: 'add' | 'edit', record?: any) => {
    setModalMode(mode);
    if (mode === 'edit' && record) {
      let response;
      try {
        switch (activeTabKey) {
          case '1':
            response = await getExistingRolesCost(id, record.partnerId);
            break;
          case '2':
            response = await getExistingRolesInvoice(id, record.partnerId);
            break;
          case '3':
            response = await getExistingRolesReport(id, record.reportId);
            break;
          default:
            response = null;
        }

        const data = response?.data?.data;
        setPartnerData({
          id: data.id,
          partnerName: record.partnerName,
          read: data.read,
          create: data.create,
          update: data.update,
          delete: data.delete,
          partnerId: data.partnerId,
          reportId: data.reportId,
        });
      } catch (error: any) {
        message.error(error?.response?.data.Message);
      }
    } else {
      setPartnerData(initialPartnerState);
    }
    setIsDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
  };

  const [accessForAllState, setAccessForAllState] = useState({
    read: false,
    create: false,
    update: false,
    delete: false,
  });

  const handleAccessForAllChange = async (action: string, checked: boolean) => {
    const endpointMap: { [key: string]: any } = {
      '2-read': setForAllViewInvoice,
      '2-create': setForAllAddInvoice,
      '2-update': setForAllUpdateInvoice,
      '2-delete': setForAllDeleteInvoice,

      '1-read': setForAllViewCost,
      '1-create': setForAllAddCost,
      '1-update': setForAllUpdateCost,
      '1-delete': setForAllDeleteCost,

      '3-read': setForAllViewReport,
    };

    const endpointKey = `${activeTabKey}-${action}`;
    const selectedEndpoint = endpointMap[endpointKey];

    if (!selectedEndpoint) {
      message.error(`${translation.subusers.error2}`);
      return;
    }

    setAccessForAllState((prevState) => ({
      ...prevState,
      [action]: checked,
    }));

    try {
      await selectedEndpoint(id, checked);

      message.success(`${translation.subusers.updateSucces4}`);
      await fetchPermissionsData();
    } catch (error: any) {
      setAccessForAllState((prevState) => ({
        ...prevState,
        [action]: !checked,
      }));
      message.error(
        error?.response?.data.Message
          ? error?.response?.data.Message
          : 'Permission required!'
      );
    }
  };

  useEffect(() => {
    const fetchAccessForAllState = async () => {
      try {
        let result;
        switch (activeTabKey) {
          case '1': // Cost Tab
            result = await getCostCheckRoleEquivalents(id);
            break;
          case '2': // Invoice Tab
            result = await getInvoiceCheckRoleEquivalents(id);
            break;
          case '3': // Report Tab
            result = await getReportCheckRoleEquivalents(id);
            break;
          default:
            result = {};
        }
        const permissionData = result?.data?.data || {};
        setAccessForAllState({
          read: permissionData.readRoleCheckBoxIsEnabled || false,
          create: permissionData.createRoleCheckBoxIsEnabled || false,
          update: permissionData.updateRoleCheckBoxIsEnabled || false,
          delete: permissionData.deleteRoleCheckBoxIsEnabled || false,
        });
      } catch (error: any) {
        message.error(error?.response?.data.Message);
      }
    };

    if (id) {
      fetchAccessForAllState();
    }
  }, [activeTabKey, id]);

  const AccessForAllPartners: React.FC = () => (
    <tr style={{ backgroundColor: 'rgb(70, 106, 251)' }}>
      <th
        style={{
          padding: '10px',
          fontWeight: '600',
          textAlign: 'center',
          color: 'white',
          opacity: 1,
        }}
      >
        {translation.subusers.accessForAll}
      </th>
      {activeTabKey === '3' ? (
        <th style={{ textAlign: 'center', opacity: 1 }}>
          <Checkbox
            checked={accessForAllState['read']}
            onChange={(e) => handleAccessForAllChange('read', e.target.checked)}
          />
        </th>
      ) : (
        ['read', 'create', 'update', 'delete'].map((action) => (
          <th style={{ textAlign: 'center', opacity: 1 }} key={action}>
            <Checkbox
              checked={accessForAllState[action]}
              onChange={(e) =>
                handleAccessForAllChange(action, e.target.checked)
              }
            />
          </th>
        ))
      )}
      <th style={{ textAlign: 'center', opacity: 1 }}></th>
    </tr>
  );

  const setLang = localStorage.getItem('language');

  return (
    <div>
      <div>
        <StyledBtn onClick={() => navigate('/profile/subuser')}>
          <LeftOutlined /> {translation.subusers.back}
        </StyledBtn>

        <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>
          {setLang === 'az'
            ? selectedPartner + ' ' + translation.subusers.role_header
            : translation.subusers.role_header + ' ' + selectedPartner}
        </h1>

        <div style={{ marginTop: '20px' }}>
          <PermissionsContainer>
            <RoleContainer>
              {groupedRoles &&
                Object.keys(groupedRoles).map((roleGroup, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px dashed #1890ff',
                      padding: '10px 20px',
                    }}
                    className="w-[100%] md:w-[50%] lg:w-[20%]"
                  >
                    <RoleTitle>{roleGroup}</RoleTitle>
                    {groupedRoles[roleGroup].map((permission, index) => (
                      <PermissionRow key={index}>
                        <PermissionName>{permission}</PermissionName>

                        <Checkbox
                          checked={
                            permissions[roleGroup]?.[permission] ??
                            roleById?.some(
                              (role) => role.roleName === permission
                            )
                          }
                          style={{ marginLeft: '20px' }}
                          onChange={(e) => {
                            const newCheckedValue = e.target.checked;
                            handlePermissionChange(
                              roleGroup,
                              permission,
                              newCheckedValue
                            );
                          }}
                        />
                      </PermissionRow>
                    ))}
                  </div>
                ))}
            </RoleContainer>
            <Button
              style={{
                gap: 15,
                backgroundColor: '#466AFB',
                padding: '0 30px',
              }}
              posLeft
              onClick={handleSavePermissions}
            >
              {translation.subusers.save}
            </Button>
            <Tabs
              style={{ width: '100%' }}
              defaultActiveKey="1"
              onChange={onTabChange}
            >
              <Tabs.TabPane
                tab={translation.subusers.costPartnersAccesses}
                key="1"
              >
                <div className="mb-[10px] flex flex-wrap items-center gap-[10px]">
                  <button
                    onClick={handleAddNew}
                    className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
                  >
                    <PlusOutlined style={{ marginRight: '10px' }} />{' '}
                    {translation.subusers.addNew}
                  </button>
                  <Popconfirm
                    title={translation.subusers.deleteConfirm}
                    onConfirm={handleDeleteAll}
                    okText={translation.InvoiceDetails.Yes}
                    cancelText={translation.InvoiceDetails.No}
                  >
                    <button
                      onClick={handleAddNew}
                      className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-red-600 px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
                    >
                      <DeleteOutlined style={{ marginRight: '10px' }} />{' '}
                      {translation.subusers.deleteAll}
                    </button>
                  </Popconfirm>
                </div>
                <TableScrollWrapper>
                  <Table
                    pagination={false}
                    dataSource={permissionsData}
                    columns={getColumns(activeTabKey)}
                    rowKey="userId"
                    scroll={{ x: 900 }}
                    components={{
                      header: {
                        wrapper: (props) => (
                          <>
                            <AccessForAllPartners />
                            <thead {...props} />
                          </>
                        ),
                      },
                    }}
                  />
                </TableScrollWrapper>
                {permissionsData.length > 0 && (
                  <Pagination
                    current={pageIndex + 1}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    onShowSizeChange={(current, size) => {
                      setPageSize(size);
                      setPageIndex(0);
                    }}
                    responsive={true}
                    style={{ marginTop: '16px', textAlign: 'right' }}
                  />
                )}
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={translation.subusers.invoicePartnersAccesses}
                key="2"
              >
                <button
                  style={{
                    margin: '10px',
                    background: 'rgb(70, 106, 251)',
                    color: 'white',
                    padding: '5px 10px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                  }}
                  onClick={handleAddNew}
                >
                  <PlusOutlined style={{ marginRight: '10px' }} />{' '}
                  {translation.subusers.addNew}
                </button>

                <Popconfirm
                  title={translation.subusers.deleteConfirm}
                  onConfirm={handleDeleteAll}
                  okText={translation.InvoiceDetails.Yes}
                  cancelText={translation.InvoiceDetails.No}
                >
                  <button
                    style={{
                      margin: '10px',
                      background: 'red',
                      color: 'white',
                      padding: '5px 10px',
                      fontWeight: 'bold',
                      borderRadius: '4px',
                    }}
                  >
                    <DeleteOutlined style={{ marginRight: '10px' }} />{' '}
                    {translation.subusers.deleteAll}
                  </button>
                </Popconfirm>
                <TableScrollWrapper>
                  <Table
                    pagination={false}
                    dataSource={permissionsData}
                    columns={getColumns(activeTabKey)}
                    rowKey="userId"
                    scroll={{ x: 900 }}
                    components={{
                      header: {
                        wrapper: (props) => (
                          <>
                            <AccessForAllPartners />
                            <thead {...props} />
                          </>
                        ),
                      },
                    }}
                  />
                </TableScrollWrapper>

                {permissionsData.length > 0 && (
                  <Pagination
                    current={pageIndex + 1}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    onShowSizeChange={(current, size) => {
                      setPageSize(size);
                      setPageIndex(0);
                    }}
                    responsive={true}
                    style={{ marginTop: '16px', textAlign: 'right' }}
                  />
                )}
              </Tabs.TabPane>
              <Tabs.TabPane tab={translation.subusers.reportAccesses} key="3">
                <button
                  style={{
                    margin: '10px',
                    background: 'rgb(70, 106, 251)',
                    color: 'white',
                    padding: '5px 10px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                  }}
                  onClick={handleAddNew}
                >
                  <PlusOutlined style={{ marginRight: '10px' }} />{' '}
                  {translation.subusers.addNew}
                </button>

                <Popconfirm
                  title={translation.subusers.deleteConfirm}
                  onConfirm={handleDeleteAll}
                  okText={translation.InvoiceDetails.Yes}
                  cancelText={translation.InvoiceDetails.No}
                >
                  <button
                    style={{
                      margin: '10px',
                      background: 'red',
                      color: 'white',
                      padding: '5px 10px',
                      fontWeight: 'bold',
                      borderRadius: '4px',
                    }}
                  >
                    <DeleteOutlined style={{ marginRight: '10px' }} />{' '}
                    {translation.subusers.deleteAll}
                  </button>
                </Popconfirm>
                <TableScrollWrapper>
                  <Table
                    pagination={false}
                    dataSource={permissionsData}
                    columns={getColumns(activeTabKey)}
                    rowKey="userId"
                    scroll={{ x: 900 }}
                    components={{
                      header: {
                        wrapper: (props) => (
                          <>
                            <AccessForAllPartners />
                            <thead {...props} />
                          </>
                        ),
                      },
                    }}
                  />
                </TableScrollWrapper>

                {permissionsData.length > 0 && (
                  <Pagination
                    current={pageIndex + 1}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    onShowSizeChange={(current, size) => {
                      setPageSize(size);
                      setPageIndex(0);
                    }}
                    responsive={true}
                    style={{ marginTop: '16px', textAlign: 'right' }}
                  />
                )}
              </Tabs.TabPane>
            </Tabs>
          </PermissionsContainer>
        </div>
      </div>

      <PermissionSubUserForm
        modalMode={modalMode}
        handleSave={handleSavePermissionsModal}
        open={isDrawerVisible}
        handleClose={handleDrawerClose}
        activeTabKey={activeTabKey}
        partnerData={partnerData}
        handleInputChange={handleInputChange}
        reports={reports}
        debitors={debitors}
        creditors={creditors}
        partners={partners}
      />
    </div>
  );
};

const StyledBtn = styled(AntButton)`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const PermissionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 10px 0 20px 0;
`;

const RoleContainer = styled.div`
  display: flex;
  padding: 10px;
  background-color: #fafafa;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 100%;
  flex-wrap: wrap;
`;

const RoleTitle = styled.div`
  font-weight: bold;
  margin-top: 15px;
  font-size: 18px;
  text-align: left;
`;

const PermissionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
`;

const PermissionName = styled.span`
  flex: 1;
`;

const ReadOnlyCheckbox = styled(AntCheckbox)<{ $isReadOnly?: boolean }>`
  ${(props) =>
    props.$isReadOnly &&
    `
    pointer-events: none; 
    cursor: not-allowed;  
    .ant-checkbox-inner {
      border-color: #1890ff; 
    }
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #1890ff; 
      border-color: #1890ff; 
    }
  `}
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
`;

export default PermissionSubUsers;
